import {
  Control,
  FieldValues,
  Path,
  useFormContext,
  useWatch,
} from "react-hook-form";

import { FormField, FormItem, FormMessage } from "@/components/ui/form";

import { TeamOption, TeamRosterPicker } from "./TeamRosterPicker";

type OverviewTeamSelectProps<TFormValues extends FieldValues> = {
  label: string;
  required?: boolean;
  optional?: boolean;
  fieldName: Path<TFormValues>;
  control: Control<TFormValues>;
  submitted: boolean;
  initialLabel?: string;
  initialParentName?: string | null;
  onTeamChange?: (option: TeamOption | null, visibilityGroup: string) => void;
  colorFieldName: Path<TFormValues>;
  onColorChange?: (color: string) => void;
};

export function OverviewTeamSelect<TFormValues extends FieldValues>({
  label,
  required,
  optional,
  fieldName,
  control,
  submitted,
  initialLabel,
  initialParentName,
  onTeamChange,
  colorFieldName,
  onColorChange,
}: OverviewTeamSelectProps<TFormValues>) {
  const fieldValue = useWatch({ control, name: fieldName });
  const colorValue = useWatch({ control, name: colorFieldName });
  const form = useFormContext<TFormValues>();

  return (
    <FormField
      control={control}
      name={fieldName}
      render={({ field }) => (
        <FormItem>
          <TeamRosterPicker
            label={label}
            required={required}
            optional={optional}
            value={(fieldValue as string | undefined) ?? null}
            initialLabel={initialLabel}
            initialParentName={initialParentName}
            onConfirm={(option, visibilityGroup) => {
              field.onChange(option?.value ?? "");
              onTeamChange?.(option, visibilityGroup);
            }}
            onClear={() => {
              field.onChange("");
              onTeamChange?.(null, "");
            }}
            messageSlot={submitted ? <FormMessage /> : null}
            teamColor={(colorValue as string | undefined) ?? undefined}
            onColorChange={(color: string) => {
              if (form) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                form.setValue(colorFieldName as any, color as any, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
                onColorChange?.(color);
              }
            }}
          />
        </FormItem>
      )}
    />
  );
}
